import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Edit3, Trash2, Plus, Lock, Check, X, Search, Info, Loader2 } from "lucide-react";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { applyFieldOptions, applyEhiFieldOptions } from "@/lib/leadFieldOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SCOPES = {
  lead: { base: `${API}/admin/field-options`, apply: applyFieldOptions, first: "specialty", noun: "lead", where: "Lead → Information" },
  ehi: { base: `${API}/admin/ehi-field-options`, apply: applyEhiFieldOptions, first: "productType", noun: "client", where: "EHI Client → Information" },
};
const enc = (v) => encodeURIComponent(v);
const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  return (typeof d === "string" ? d : d?.message) || fb;
};
const S = {
  input: { fontSize: 13, padding: "7px 10px", border: "1px solid var(--border, #e5e5e5)", borderRadius: 6, outline: "none", background: "#fff" },
  iconBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, border: "1px solid transparent", background: "transparent", cursor: "pointer", color: "#555", transition: "background-color 120ms, color 120ms" },
  pill: { fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "#F3F4F6", color: "#4B5563" },
};

function IconBtn({ testid, title, onClick, disabled, children, danger }) {
  return (
    <button type="button" data-testid={testid} title={title} aria-label={title} onClick={onClick} disabled={disabled}
      className="fo-icon-btn" style={{ ...S.iconBtn, opacity: disabled ? 0.3 : 1, cursor: disabled ? "not-allowed" : "pointer", color: danger ? "#B42318" : "#555" }}>
      {children}
    </button>
  );
}

function FieldPicker({ fields, value, onChange }) {
  const groups = useMemo(() => {
    const g = {};
    fields.forEach((f) => { (g[f.section] = g[f.section] || []).push(f); });
    return Object.entries(g);
  }, [fields]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="field-options-field-select" style={{ width: 340, fontSize: 13 }}>
        <SelectValue placeholder="Choose a field" />
      </SelectTrigger>
      <SelectContent style={{ maxHeight: 420 }}>
        {groups.map(([section, items]) => (
          <SelectGroup key={section}>
            <SelectLabel style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.6, color: "#888" }}>{section}</SelectLabel>
            {items.map((f) => (
              <SelectItem key={f.field_key} value={f.field_key} data-testid={`field-options-field-item-${f.field_key}`}>
                {f.label} <span style={{ color: "#999", fontSize: 11 }}>({f.options.length})</span>
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
      </SelectContent>
    </Select>
  );
}

function OptionRow({ field, value, index, total, busy, filtering, onMove, onRename, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const locked = field.locked.includes(value);
  const editable = !field.readonly && !locked;
  const slug = index;
  useEffect(() => { setDraft(value); }, [value]);
  const submit = async () => {
    const ok = await onRename(value, draft);
    if (ok) setEditing(false);
  };
  return (
    <tr data-testid={`field-option-row-${slug}`} className="fo-row" style={{ borderTop: "1px solid var(--border, #f0f0f0)" }}>
      <td style={{ padding: "8px 10px", width: 44, color: "#999", fontSize: 12, fontVariantNumeric: "tabular-nums" }}>{index + 1}</td>
      <td style={{ padding: "8px 10px", fontSize: 13 }}>
        {editing ? (
          <input data-testid={`field-option-edit-input-${slug}`} autoFocus value={draft} maxLength={120}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
            style={{ ...S.input, width: "100%" }} />
        ) : (
          <span data-testid={`field-option-value-${slug}`} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {value}
            {locked && (
              <span data-testid={`field-option-locked-${slug}`} title={field.lock_reason || "System value"} style={{ ...S.pill, display: "inline-flex", alignItems: "center", gap: 4, background: "#FEF3C7", color: "#92400E" }}>
                <Lock style={{ width: 9, height: 9 }} /> System
              </span>
            )}
          </span>
        )}
      </td>
      <td style={{ padding: "6px 10px", width: 170, textAlign: "right", whiteSpace: "nowrap" }}>
        {editing ? (
          <>
            <IconBtn testid={`field-option-save-${slug}`} title="Save" onClick={submit} disabled={busy}><Check style={{ width: 14, height: 14 }} /></IconBtn>
            <IconBtn testid={`field-option-cancel-${slug}`} title="Cancel" onClick={() => { setDraft(value); setEditing(false); }}><X style={{ width: 14, height: 14 }} /></IconBtn>
          </>
        ) : !field.readonly && (
          <>
            {!filtering && <IconBtn testid={`field-option-up-${slug}`} title="Move up" onClick={() => onMove(index, -1)} disabled={busy || index === 0}><ArrowUp style={{ width: 14, height: 14 }} /></IconBtn>}
            {!filtering && <IconBtn testid={`field-option-down-${slug}`} title="Move down" onClick={() => onMove(index, 1)} disabled={busy || index === total - 1}><ArrowDown style={{ width: 14, height: 14 }} /></IconBtn>}
            <IconBtn testid={`field-option-edit-${slug}`} title={editable ? "Rename" : "System values can't be renamed"} onClick={() => { setDraft(value); setEditing(true); }} disabled={busy || !editable}><Edit3 style={{ width: 13, height: 13 }} /></IconBtn>
            <IconBtn testid={`field-option-delete-${slug}`} title={editable ? "Remove" : "System values can't be removed"} onClick={() => onDelete(value)} disabled={busy || !editable} danger><Trash2 style={{ width: 13, height: 13 }} /></IconBtn>
          </>
        )}
      </td>
    </tr>
  );
}

function RenameDialog({ pending, busy, noun, onCancel, onConfirm }) {
  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="field-option-rename-dialog">
        <DialogHeader>
          <DialogTitle>Rename and update {noun}s?</DialogTitle>
          <DialogDescription>
            <b data-testid="field-option-rename-count">{pending?.count}</b> {noun}(s) currently use "{pending?.from}". They will all be updated to "{pending?.to}".
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button type="button" className="btn" data-testid="field-option-rename-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" data-testid="field-option-rename-confirm" disabled={busy} onClick={onConfirm}>
            {busy ? "Updating…" : `Rename and update ${noun}s`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ pending, options, busy, noun, onCancel, onConfirm }) {
  const [target, setTarget] = useState("");
  useEffect(() => { setTarget(""); }, [pending]);
  const others = options.filter((o) => o !== pending?.value);
  return (
    <Dialog open={!!pending} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="field-option-delete-dialog">
        <DialogHeader>
          <DialogTitle>"{pending?.value}" is in use</DialogTitle>
          <DialogDescription>
            <b data-testid="field-option-delete-count">{pending?.count}</b> {noun}(s) use this value. Choose a value to move them to before it's removed.
          </DialogDescription>
        </DialogHeader>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger data-testid="field-option-replace-select" style={{ fontSize: 13 }}>
            <SelectValue placeholder={`Move ${noun}s to…`} />
          </SelectTrigger>
          <SelectContent style={{ maxHeight: 320 }}>
            {others.map((o, i) => <SelectItem key={o} value={o} data-testid={`field-option-replace-item-${i}`}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <DialogFooter>
          <button type="button" className="btn" data-testid="field-option-delete-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" data-testid="field-option-delete-confirm" disabled={!target || busy} onClick={() => onConfirm(target)}>
            {busy ? "Moving…" : `Move ${noun}s and remove`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldOptionsEditor({ token, scope }) {
  const { base: BASE, apply, first, noun, where } = SCOPES[scope];
  const [fields, setFields] = useState(null);
  const [selected, setSelected] = useState(first);
  const [newValue, setNewValue] = useState("");
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [renamePending, setRenamePending] = useState(null);
  const [deletePending, setDeletePending] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(BASE, { headers })
      .then((r) => setFields(r.data.fields || []))
      .catch((e) => { setFields([]); toast.error(errMsg(e, "Failed to load field options")); });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the token changes
  }, [token]);

  const field = fields?.find((f) => f.field_key === selected);
  const setOptions = (key, options) => {
    setFields((fs) => fs.map((f) => (f.field_key === key ? { ...f, options } : f)));
    apply({ [key]: options });
  };
  const usageOf = async (value) => (await axios.get(`${BASE}/${selected}/usage`, { headers, params: { option: value } })).data.usage_count;

  const add = async () => {
    const v = newValue.trim();
    if (!v) { toast.error("Enter a value first"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${BASE}/${selected}`, { option: v }, { headers });
      setOptions(selected, r.data.options); setNewValue(""); toast.success(`Added "${v}"`);
    } catch (e) { toast.error(errMsg(e, "Couldn't add value")); } finally { setBusy(false); }
  };

  const move = async (index, dir) => {
    const prev = field.options;
    const next = [...prev];
    [next[index], next[index + dir]] = [next[index + dir], next[index]];
    setOptions(selected, next);
    setBusy(true);
    try { await axios.put(`${BASE}/${selected}/order`, { options: next }, { headers }); }
    catch (e) { setOptions(selected, prev); toast.error(errMsg(e, "Couldn't reorder")); }
    finally { setBusy(false); }
  };

  const doRename = async (from, to) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${BASE}/${selected}/${enc(from)}`, { new_value: to }, { headers });
      setOptions(selected, r.data.options);
      toast.success(r.data.updated_leads ? `Renamed and updated ${r.data.updated_leads} ${noun}(s)` : "Renamed");
      return true;
    } catch (e) { toast.error(errMsg(e, "Couldn't rename")); return false; } finally { setBusy(false); setRenamePending(null); }
  };

  const rename = async (from, draft) => {
    const to = draft.trim();
    if (!to) { toast.error("Value can't be empty"); return false; }
    if (to === from) return true;
    if (field.options.some((o) => o !== from && o.toLowerCase() === to.toLowerCase())) { toast.error(`"${to}" already exists in this list`); return false; }
    try {
      const count = await usageOf(from);
      if (count > 0) { setRenamePending({ from, to, count }); return true; }
    } catch (e) { toast.error(errMsg(e, "Couldn't check usage")); return false; }
    return doRename(from, to);
  };

  const doDelete = async (value, replaceWith) => {
    setBusy(true);
    try {
      const r = await axios.delete(`${BASE}/${selected}/${enc(value)}`, { headers, params: replaceWith ? { replace_with: replaceWith } : {} });
      setOptions(selected, r.data.options);
      toast.success(r.data.usage_count ? `Moved ${r.data.usage_count} ${noun}(s) to "${replaceWith}" and removed "${value}"` : `Removed "${value}"`);
      setDeletePending(null);
    } catch (e) { toast.error(errMsg(e, "Couldn't remove value")); } finally { setBusy(false); }
  };

  const remove = async (value) => {
    try {
      const count = await usageOf(value);
      if (count > 0) setDeletePending({ value, count });
      else await doDelete(value);
    } catch (e) { toast.error(errMsg(e, "Couldn't check usage")); }
  };

  if (!fields) return <div data-testid="field-options-loading" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#888" }}><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Loading field options…</div>;

  const q = filter.trim().toLowerCase();
  const rows = (field?.options || []).map((v, i) => ({ v, i })).filter((r) => !q || r.v.toLowerCase().includes(q));

  return (
    <div data-testid={`field-options-editor-${scope}`}>
      <style>{`.fo-icon-btn:not(:disabled):hover{background:#F3F4F6;color:#111}.fo-row{transition:background-color 120ms}.fo-row:hover{background:#FAFAFA}`}</style>
      <p data-testid="field-options-description" style={{ fontSize: 12, color: "#777", margin: "0 0 14px" }}>Manage the dropdown values reps pick from on {where}. Changes apply right away.</p>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <FieldPicker fields={fields} value={selected} onChange={(k) => { setSelected(k); setFilter(""); setNewValue(""); }} />
        {field && <span data-testid="field-options-count" style={S.pill}>{field.options.length} values</span>}
        {field?.updated_by_name && <span data-testid="field-options-updated" style={{ fontSize: 11, color: "#999" }}>Last edited by {field.updated_by_name}{field.updated_at ? ` · ${new Date(field.updated_at).toLocaleString()}` : ""}</span>}
      </div>
      {field?.readonly && (
        <div data-testid="field-options-readonly-banner" style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12, padding: "8px 12px", borderRadius: 6, background: "#EFF6FF", color: "#1E40AF", marginBottom: 12 }}>
          <Info style={{ width: 14, height: 14 }} /> This list is set automatically by the system, so it's view only.
        </div>
      )}
      {field && !field.readonly && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <input data-testid="field-options-new-input" placeholder={`Add a new ${field.label} value`} value={newValue} maxLength={120}
            onChange={(e) => setNewValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} style={{ ...S.input, flex: 1, maxWidth: 420 }} />
          <button type="button" className="btn btn-primary" data-testid="field-options-add-btn" onClick={add} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Plus style={{ width: 13, height: 13 }} /> Add New
          </button>
        </div>
      )}
      {field && field.options.length > 8 && (
        <div style={{ position: "relative", maxWidth: 280, marginBottom: 10 }}>
          <Search style={{ position: "absolute", left: 9, top: 9, width: 13, height: 13, color: "#999" }} />
          <input data-testid="field-options-filter-input" placeholder="Filter values" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ ...S.input, width: "100%", paddingLeft: 28 }} />
        </div>
      )}
      {field && (
        <div style={{ border: "1px solid var(--border, #eee)", borderRadius: 8, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="field-options-table">
            <thead>
              <tr style={{ background: "#FAFAFA", fontSize: 11, color: "#777", textAlign: "left", textTransform: "uppercase", letterSpacing: 0.4 }}>
                <th style={{ padding: "8px 10px" }}>#</th><th style={{ padding: "8px 10px" }}>Value</th><th style={{ padding: "8px 10px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ v, i }) => (
                <OptionRow key={v} field={field} value={v} index={i} total={field.options.length} busy={busy} filtering={!!q}
                  onMove={move} onRename={rename} onDelete={remove} />
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={3} data-testid="field-options-empty" style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#999" }}>
                  {q ? "No values match your filter." : "No values yet. Add the first one above."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <RenameDialog pending={renamePending} busy={busy} noun={noun} onCancel={() => setRenamePending(null)} onConfirm={() => doRename(renamePending.from, renamePending.to)} />
      <DeleteDialog pending={deletePending} options={field?.options || []} busy={busy} noun={noun} onCancel={() => setDeletePending(null)} onConfirm={(t) => doDelete(deletePending.value, t)} />
    </div>
  );
}

const SWITCH_OPTS = [["lead", "Leads"], ["ehi", "EHI Clients"]];

export default function FieldOptionsPanel({ token }) {
  const [scope, setScope] = useState("lead");
  return (
    <div data-testid="field-options-panel">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Field Options</h3>
        <div role="tablist" data-testid="field-options-scope-switch" style={{ display: "inline-flex", padding: 3, borderRadius: 999, background: "#F3F4F6" }}>
          {SWITCH_OPTS.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={scope === id} data-testid={`field-options-scope-${id}`} onClick={() => setScope(id)}
              style={{ fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 999, border: "none", cursor: "pointer",
                background: scope === id ? "#fff" : "transparent", color: scope === id ? "#111" : "#6B7280",
                boxShadow: scope === id ? "0 1px 2px rgba(0,0,0,0.08)" : "none", transition: "background-color 120ms, color 120ms" }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <FieldOptionsEditor key={scope} token={token} scope={scope} />
    </div>
  );
}
